import axios, { AxiosError, AxiosInstance } from 'axios';
import { Api } from '@sodamnfoolish/sjc-backend-types';
import { Source, Post } from '@sodamnfoolish/sjc-backend-types/src/api/feed/dto';
import { PumpFunBubble } from '@sodamnfoolish/sjc-backend-types/src/api/pump/dto';
import { LetsBonkBubble } from '@sodamnfoolish/sjc-backend-types/src/api/bonk/dto';
import { TwitterUserInfo } from '@sodamnfoolish/sjc-backend-types/src/api/twitter/dto';

export interface UploadMetadataArgs {
    file: File;
    name: string;
    symbol: string;
    description: string;
    twitter: string;
    telegram: string;
    website: string;
    showName: string;
}

export interface UploadMetadataResponse {
    metadata: {
        name: string;
        symbol: string;
        description: string;
        image: string;
        showName: boolean;
        createdOn: string;
        twitter: string;
        telegram: string;
        website: string;
    };
    metadataUri: string;
}

export class ParserBackend {
    private readonly client: AxiosInstance;

    constructor(url: string) {
        this.client = axios.create({
            baseURL: url,
            withCredentials: true,
        });
    }

    public async register(accessCode: string, successRedirectUrl: string, failureRedirectUrl: string): Promise<void> {
        window.location.href = this.client.getUri({
            url: `/auth/register`,
            params: {
                accessCode,
                successRedirectUrl,
                failureRedirectUrl,
            }
        });
    }

    public async login(successRedirectUrl: string, failureRedirectUrl: string): Promise<void> {
        window.location.href = this.client.getUri({
            url: `/auth/login`,
            params: {
                successRedirectUrl,
                failureRedirectUrl,
            }
        });
    }

    public async me(): Promise<Api.Auth.User> {
        const res = await this.client.get<Api.Auth.User>(`/auth/me`);
        return res.data;
    }

    public async logout(redirectUrl: string): Promise<void> {
        window.location.href = this.client.getUri({
            url: `/auth/logout`,
            params: {
                redirectUrl
            }
        });
    }

    public getSources = (): Promise<Source[]> =>
        this.client.get(`/feed/sources`)
            .then((res) => res.data);

    public getPumpFunBubbles = (twitterUsername?: string, size = 20): Promise<PumpFunBubble[]> =>
        this.client.get(`/pump/bubbles`, {
            params: {
                twitterUsername: twitterUsername,
                size: size,
            },
        }).then((res) => res.data);

    public getBonkBubbles = (twitterUsername?: string, size = 20): Promise<LetsBonkBubble[]> =>
        this.client.get(`/bonk/bubbles`, {
            params: {
                twitterUsername: twitterUsername,
                size: size,
            },
        }).then((res) => res.data);

    public getFeed = (sources: Source[], size = 20): Promise<Post[]> =>
        this.client.get(`/feed/`, {
            params: {
                sources: JSON.stringify(sources),
                size: size,
            },
        })
            .then((res) => {
                return res.data;
            });

    public getTwitterUserInfoByUsername = (username: string): Promise<TwitterUserInfo> =>
        this.client.get(`/twitter/users/${username}/info`)
            .then((res) => {
                return res.data;
            })
            .catch((error: AxiosError) => {
                console.log(error);
            });

    public getMedia = (mediaId: string): Promise<Blob> =>
        this.client.get(`/feed/media/${mediaId}`, {
            responseType: 'blob',
        })
            .then((res) => res.data);

    public getChatGptMetadataFromPostId = (postId: string): Promise<Api.AI.DTO.Metadata> =>
        this.client.post(
            `/ai/generate-token-metadata-by-post-id/${postId}`,
            {},
        )
            .then((res) => res.data);

    public generateTokenMetadataByTweetUrl = (encodedTweetUrl: string): Promise<Api.AI.DTO.Metadata> =>
        this.client.post(
            `/ai/generate-token-metadata-by-tweet-url/${encodedTweetUrl}`,
            {},
        )
            .then((res) => res.data);

    public generateTokenMetadataByUrl = (ecodedUrl: string): Promise<Api.AI.DTO.Metadata> =>
        this.client.post(
            `/ai/generate-token-metadata-by-url/${ecodedUrl}`,
            {},
        )
            .then((res) => res.data);

    public generateImage = (generateImageRequest: Api.AI.DTO.GenerateImageRequest): Promise<string> =>
        this.client.post(`/ai/generate-image`, generateImageRequest)
            .then((res) => res.data)
            .catch((error: AxiosError) => {
                throw (error.response?.data as any).message.replace(/400 /g, '');
            });

    public getTokenMetadata = (mint: string): Promise<Api.Token.DTO.Metadata> =>
        this.client.get(`/token/${mint}/fetch`)
            .then((res) => res.data);

    public deployWithSnipe = async (data: Api.Wallet.DTO.DeployWithSniperRequest): Promise<Api.Wallet.DTO.DeployWithSniperResponse> =>
        this.client.post<Api.Wallet.DTO.DeployWithSniperResponse>(`/wallet/deploy-with-sniper`, data)
            .then((res) => res.data);

    public async uploadMetadata(args: UploadMetadataArgs): Promise<UploadMetadataResponse> {
        const data = new FormData();
        Object.entries(args).forEach(([key, value]) => data.append(key, value));

        const res = await this.client.post<UploadMetadataResponse>(`pump/ipfs`, data, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return res.data;
    }
}
